import { io, Socket } from 'socket.io-client';
import type {
  PriceResponseDto,
  WsClientToServerEvents,
  WsServerToClientEvents,
  WsServerConnectedPayload,
  WsServerErrorPayload,
} from '../api-schema/generated/types';
import { WS_CONFIG } from '../api-schema/generated/types';

type TypedSocket = Socket<WsServerToClientEvents, WsClientToServerEvents>;
type PriceUpdateCallback = (data: PriceResponseDto) => void;
type ConnectionStateCallback = (state: ConnectionState) => void;

export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
}

interface Subscription {
  symbol: string;
  callbacks: PriceUpdateCallback[];
}

/**
 * Strip exchange prefix from symbol. e.g. "HSX:VNM" → "VNM", "VNM" → "VNM"
 */
function stripExchangePrefix(symbol: string): string {
  const idx = symbol.indexOf(':');
  return idx >= 0 ? symbol.substring(idx + 1) : symbol;
}

class WebSocketService {
  private socket: TypedSocket | null = null;
  private url: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 3000;
  private subscriptions: Map<string, Subscription> = new Map();
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private stateCallbacks: ConnectionStateCallback[] = [];
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private authenticated: boolean = false;
  private userId: string | null = null;

  constructor(url?: string) {
    this.url = url || process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:3000';
  }

  connect(): void {
    // Idempotent on socket EXISTENCE, not on `connected`. Three hooks
    // (useLivePrices / useVn30Price / useWebSocketPriceData) each call connect()
    // on mount; the old `connected` guard was still false during the handshake,
    // so every caller built its own io() socket and orphaned the previous one —
    // orphans stay open, re-authenticate, and keep firing price_update into the
    // same callbacks. socket.io drives reconnects itself (reconnection: true).
    if (this.socket) return;

    this.updateConnectionState(ConnectionState.CONNECTING);
    this.authenticated = false;

    try {
      const token = localStorage.getItem('access_token');

      this.socket = io(`${this.url}${WS_CONFIG.namespace}`, {
        auth: { token },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
      }) as TypedSocket;

      this.socket.on('connect', () => {
        this.reconnectAttempts = 0;
      });

      this.socket.on('connected', (data: WsServerConnectedPayload) => {
        this.authenticated = true;
        this.userId = data?.userId ?? null;
        this.updateConnectionState(ConnectionState.CONNECTED);
        this.resubscribeAll();
      });

      this.socket.on('subscribed', () => {});

      this.socket.on('unsubscribed', () => {});

      this.socket.on('price_update', (data: PriceResponseDto) => {
        this.handlePriceUpdate(data);
      });

      this.socket.on('error', (data: WsServerErrorPayload) => {
        console.error('WebSocket server error:', data?.message);
      });

      (this.socket as Socket).on('connect_error', (error: Error) => {
        console.error('WebSocket connect error:', error.message);
        this.updateConnectionState(ConnectionState.ERROR);
      });

      this.socket.on('disconnect', () => {
        this.authenticated = false;
        this.userId = null;
        this.updateConnectionState(ConnectionState.DISCONNECTED);
        // No manual reconnect here — socket.io retries on the same socket and
        // re-emits 'connected', which resubscribes. Calling connect() again
        // raced its internal loop and produced a second live socket.
      });

      // socket.io gave up after reconnectionAttempts. Drop the dead socket so
      // the existence guard in connect() stops short-circuiting, then retry on
      // our own backoff.
      (this.socket as Socket).on('reconnect_failed', () => {
        this.teardownSocket();
        this.updateConnectionState(ConnectionState.ERROR);
        this.scheduleReconnect();
      });
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.teardownSocket();
      this.updateConnectionState(ConnectionState.ERROR);
      this.scheduleReconnect();
    }
  }

  /** Detach and close the current socket so connect() can build a fresh one. */
  private teardownSocket(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.authenticated = false;
    this.userId = null;
  }

  disconnect(): void {
    this.reconnectAttempts = this.maxReconnectAttempts;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.teardownSocket();
    this.updateConnectionState(ConnectionState.DISCONNECTED);
  }

  subscribe(symbol: string, callback: PriceUpdateCallback): () => void {
    if (!this.subscriptions.has(symbol)) {
      this.subscriptions.set(symbol, { symbol, callbacks: [] });
      if (this.authenticated) {
        this.sendSubscribe(symbol);
      }
    }

    const subscription = this.subscriptions.get(symbol)!;
    subscription.callbacks.push(callback);

    return () => this.unsubscribe(symbol, callback);
  }

  unsubscribe(symbol: string, callback: PriceUpdateCallback): void {
    const subscription = this.subscriptions.get(symbol);
    if (!subscription) return;

    subscription.callbacks = subscription.callbacks.filter(cb => cb !== callback);

    if (subscription.callbacks.length === 0) {
      this.subscriptions.delete(symbol);
      if (this.authenticated) {
        this.sendUnsubscribe(symbol);
      }
    }
  }

  onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.stateCallbacks.push(callback);
    callback(this.connectionState);
    return () => {
      this.stateCallbacks = this.stateCallbacks.filter(cb => cb !== callback);
    };
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Match incoming price_update to subscriptions.
   *
   * The server emits symbol with exchange prefix (e.g. "HSX:VNM")
   * but clients may subscribe with bare symbol ("VNM") or prefixed ("HSX:VNM").
   * We try:
   *   1. Exact match on data.symbol (e.g. "HSX:VNM")
   *   2. Bare symbol from data.symbol (e.g. "VNM")
   *   3. Iterate all subscriptions checking bare-symbol equality
   */
  private handlePriceUpdate(data: PriceResponseDto): void {
    if (!data?.symbol) return;

    const incomingSymbol = data.symbol;
    const incomingBare = stripExchangePrefix(incomingSymbol);

    const matched: Subscription[] = [];

    const exact = this.subscriptions.get(incomingSymbol);
    if (exact) matched.push(exact);

    if (incomingBare !== incomingSymbol) {
      const bareMatch = this.subscriptions.get(incomingBare);
      if (bareMatch) matched.push(bareMatch);
    }

    if (matched.length === 0) {
      this.subscriptions.forEach((sub) => {
        if (stripExchangePrefix(sub.symbol) === incomingBare) {
          matched.push(sub);
        }
      });
    }

    for (const sub of matched) {
      sub.callbacks.forEach(cb => {
        try { cb(data); } catch (e) { console.error('Error in price callback:', e); }
      });
    }
  }

  private resubscribeAll(): void {
    this.subscriptions.forEach((_, symbol) => this.sendSubscribe(symbol));
  }

  /**
   * Send subscribe to server. The backend expects bare symbols (e.g. "VNM", "VN30F2511"),
   * so we strip any exchange prefix before sending.
   */
  private sendSubscribe(symbol: string): void {
    if (this.socket?.connected) {
      this.socket.emit('subscribe', { symbol: stripExchangePrefix(symbol) });
    }
  }

  private sendUnsubscribe(symbol: string): void {
    if (this.socket?.connected) {
      this.socket.emit('unsubscribe', { symbol: stripExchangePrefix(symbol) });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    this.reconnectTimeout = setTimeout(() => {
      this.teardownSocket();
      this.connect();
    }, delay);
  }

  private updateConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.stateCallbacks.forEach(cb => {
      try { cb(state); } catch (e) { console.error('Error in state callback:', e); }
    });
  }
}

const websocketService = new WebSocketService();
export default websocketService;
